import { Link } from 'react-router-dom';
import { Bell, CalendarClock, FileWarning, Gauge, KeyRound, Wallet, type LucideIcon } from 'lucide-react';
import { fechaYHora } from '@/lib/fecha';
import { formatCOP } from '@/lib/utils';
import { useAvisosDelSalon, useAvisosSinLeer, useMarcarAvisosLeidos, type AvisoDelSalon } from '@/queries/notificaciones.queries';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * La campana de la barra superior, que hasta hoy era decorativa: un botón
 * sin `onClick` sobre una tabla en la que nadie escribía nunca.
 *
 * Va aquí y no en una pantalla propia por lo de siempre: un aviso que exige
 * entrar a buscarlo no avisa a nadie. La barra está en todas las pantallas.
 */
/**
 * Qué dice cada aviso, según su plantilla.
 *
 * Antes la campanita solo conocía las reservas del portal y pintaba
 * cualquier aviso como «Nueva solicitud de reserva». Con la facturación
 * electrónica llegan otros cinco, y cada uno lleva a un sitio distinto: el
 * enlace tiene que ser una ruta que EXISTA en el router (`/facturacion`,
 * `/settings`, `/admin`), o se repite la trampa de la ruta inexistente.
 */
interface Descripcion {
  icono: LucideIcon;
  titulo: string;
  detalle: string;
  fecha: string;
  enlace: string;
  tono: 'emerald' | 'amber' | 'rose';
}

function cuandoVence(dias: number | undefined): string {
  if (dias === undefined || dias === null) return 'vence pronto';
  if (dias <= 0) return 'vence hoy';
  if (dias === 1) return 'vence mañana';
  return `vence en ${dias} días`;
}

export function describirAviso(a: AvisoDelSalon): Descripcion {
  const p = a.payload;
  const deSalon = p.tenantName ? ` · ${p.tenantName}` : '';
  const fechaAviso = fechaYHora(a.createdAt);
  switch (a.templateKey) {
    case 'fe_rechazada':
      return {
        icono: FileWarning, tono: 'rose',
        titulo: `La DIAN rechazó ${p.tipo === 'NC' ? 'la nota crédito' : 'la factura'} ${p.numero ?? ''}`.trim(),
        detalle: p.mensaje ?? 'Revisa el motivo y reinténtala.',
        fecha: fechaAviso, enlace: p.link ?? '/facturacion',
      };
    case 'fe_cupo_80':
    case 'fe_cupo_100':
      return {
        icono: Gauge, tono: 'amber',
        titulo: a.templateKey === 'fe_cupo_100'
          ? 'Llegaste al cupo de facturas del mes'
          : 'Vas en el 80 % del cupo de facturas',
        detalle: `${p.usados ?? '—'} de ${p.limite ?? '—'} este mes. Se sigue facturando: es obligatorio.`,
        fecha: fechaAviso, enlace: p.link ?? '/settings',
      };
    case 'fe_resolucion_por_vencer':
      return {
        icono: FileWarning, tono: 'amber',
        titulo: `La resolución ${p.prefijo ?? ''} de la DIAN ${cuandoVence(p.dias)}`.replace(/\s+/g, ' '),
        detalle: `Hay que cargar la nueva antes de que venza${deSalon}.`,
        fecha: fechaAviso, enlace: p.link ?? '/settings',
      };
    case 'fe_token_por_vencer':
      return {
        icono: KeyRound, tono: 'amber',
        titulo: `La conexión de facturación electrónica ${cuandoVence(p.dias)}`,
        detalle: `Netcloud tiene que renovarla${deSalon}.`,
        fecha: fechaAviso, enlace: p.link ?? '/settings',
      };
    default: {
      const conAbono = (p.depositCents ?? 0) > 0;
      return {
        icono: conAbono ? Wallet : CalendarClock, tono: 'emerald',
        titulo: conAbono
          ? `Reserva con abono de ${formatCOP(p.depositCents ?? 0)}`
          : 'Nueva solicitud de reserva',
        detalle: [p.petName ?? 'Una mascota', p.customerName, p.serviceName].filter(Boolean).join(' · '),
        fecha: p.preferredStart ? `Pidió el ${fechaYHora(p.preferredStart)}` : fechaAviso,
        enlace: p.link ?? '/agenda',
      };
    }
  }
}

const TONO_ICONO: Record<Descripcion['tono'], string> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  rose: 'bg-rose-100 text-rose-700',
};

export function CampanaDeAvisos() {
  const avisos = useAvisosDelSalon();
  const marcar = useMarcarAvisosLeidos();

  const conteo = useAvisosSinLeer();

  const lista = avisos.data ?? [];
  // La lista trae los 50 más recientes; el número sale de contar en la base,
  // que no tiene ese tope. Si el conteo falla, se usa el de la lista.
  const sinLeerEnLista = lista.filter((a) => a.readAt === null).length;
  const totalSinLeer = conteo.data ?? sinLeerEnLista;

  /**
   * Cada aviso es un ENLACE, no un botón con `navigate()`.
   *
   * Con un botón funcionaba a medias y solo se vio abriéndolo: el aviso se
   * marcaba leído y la pantalla no cambiaba. Al pulsar, el popover se cierra
   * y desmonta este componente en el mismo tic, y el `navigate()` que se
   * lanzó desde dentro se queda sin efecto. Un enlace lo resuelve el router
   * por su cuenta, no depende de que quien lo disparó siga vivo — y de paso
   * se puede abrir en otra pestaña.
   */
  const marcarSiHaceFalta = (a: AvisoDelSalon) => {
    if (a.readAt === null) marcar.mutate([a.id]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={totalSinLeer > 0
            ? `Notificaciones: ${totalSinLeer} sin leer`
            : 'Notificaciones'}
        >
          <Bell className="h-4 w-4" />
          {totalSinLeer > 0 && (
            <span
              aria-hidden
              className="absolute right-1 top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white"
            >
              {totalSinLeer > 9 ? '9+' : totalSinLeer}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[22rem] p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-sm font-semibold">
            Avisos
            {totalSinLeer > 0 && (
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                · {totalSinLeer} sin leer
              </span>
            )}
          </p>
        </div>

        {lista.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">
            No tienes avisos.
          </p>
        ) : (
          <ul className="max-h-[22rem] divide-y divide-border overflow-y-auto">
            {lista.map((a) => {
              const d = describirAviso(a);
              const Icono = d.icono;
              return (
                <li key={a.id}>
                  <Link
                    to={d.enlace}
                    onClick={() => marcarSiHaceFalta(a)}
                    className={cn(
                      'flex w-full gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/60',
                      a.readAt === null && 'bg-emerald-50/50',
                    )}
                  >
                    <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', TONO_ICONO[d.tono])}>
                      <Icono className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{d.titulo}</span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{d.detalle}</span>
                      <span className="mt-0.5 block text-[11px] text-muted-foreground">{d.fecha}</span>
                    </span>
                    {a.readAt === null && (
                      <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {totalSinLeer > 0 && (
          <div className="border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => marcar.mutate(undefined)}
              disabled={marcar.isPending}
            >
              Marcar todo leído
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
